import { MsalProvider } from '@azure/msal-react'
import { QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import { App } from '@/app/App'
import { msal } from '@/lib/auth'
import { queryClient } from '@/lib/query'
import '@/styles/tokens.css'

/**
 * Bootstrap.
 *
 * MSAL must finish handling the redirect response before anything renders. Render first
 * and the account is not yet available, so the app briefly decides nobody is signed in
 * and shows the sign-in screen to someone who just signed in.
 *
 * Written as an async function rather than top-level await, which the browser targets
 * this bundle supports do not allow.
 */
async function start(): Promise<void> {
  await msal.initialize()

  const response = await msal.handleRedirectPromise()
  if (response?.account) {
    msal.setActiveAccount(response.account)
  } else if (!msal.getActiveAccount()) {
    const [existing] = msal.getAllAccounts()
    if (existing) msal.setActiveAccount(existing)
  }

  const container = document.getElementById('root')
  if (!container) throw new Error('#root is missing from index.html')

  createRoot(container).render(
    <StrictMode>
      <MsalProvider instance={msal}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </MsalProvider>
    </StrictMode>,
  )
}

void start()
